import {
  ArrowSync20Filled,
  ArrowSync20Regular,
  Building20Filled,
  Building20Regular,
  CalendarClock20Filled,
  CalendarClock20Regular,
  CalendarLtr20Filled,
  CalendarLtr20Regular,
  Grid20Filled,
  Grid20Regular,
  History20Filled,
  History20Regular,
  Home20Filled,
  Home20Regular,
  People20Filled,
  People20Regular,
  Settings20Filled,
  Settings20Regular,
  Wrench20Filled,
  Wrench20Regular,
} from '@fluentui/react-icons';

/**
 * Navigation model shared by the side nav, the mobile bar and the user menu.
 * Keeping it in one place guarantees the three surfaces never drift apart.
 */

export interface NavItem {
  /** Route path; matched with `end` for the index route. */
  to: string;
  label: string;
  /** Short label used in the mobile bottom bar. */
  shortLabel?: string;
  icon: JSX.Element;
  activeIcon: JSX.Element;
  end?: boolean;
  adminOnly?: boolean;
}

/**
 * Routes Teams already offers as tabs in its own header.
 *
 * Inside a Teams tab these appear twice — once in the tab strip Teams draws,
 * once in our rail — and the second copy is not a shortcut, it is noise that
 * makes the app look like it does not know where it is running. Kept next to
 * the navigation model so a new tab in the manifest and a new entry here are
 * one decision, not two.
 *
 * Mirrors `staticTabs` in `teams-app/manifest/manifest.json`.
 */
export const TEAMS_TAB_ROUTES: ReadonlySet<string> = new Set([
  '/calendar',
  '/my-reservations',
  '/rooms',
]);

export const PRIMARY_NAV: readonly NavItem[] = [
  {
    to: '/',
    label: 'Inicio',
    icon: <Home20Regular />,
    activeIcon: <Home20Filled />,
    end: true,
  },
  {
    to: '/calendar',
    label: 'Calendario',
    icon: <CalendarLtr20Regular />,
    activeIcon: <CalendarLtr20Filled />,
  },
  {
    to: '/my-reservations',
    label: 'Mis reservaciones',
    shortLabel: 'Mis reservas',
    icon: <CalendarClock20Regular />,
    activeIcon: <CalendarClock20Filled />,
  },
  {
    to: '/rooms',
    label: 'Salas',
    icon: <Building20Regular />,
    activeIcon: <Building20Filled />,
  },
];

export const ADMIN_NAV: readonly NavItem[] = [
  {
    to: '/admin',
    label: 'Panel',
    icon: <Grid20Regular />,
    activeIcon: <Grid20Filled />,
    end: true,
    adminOnly: true,
  },
  {
    to: '/admin/rooms',
    label: 'Salas',
    icon: <Settings20Regular />,
    activeIcon: <Settings20Filled />,
    adminOnly: true,
  },
  {
    to: '/admin/users',
    label: 'Usuarios',
    icon: <People20Regular />,
    activeIcon: <People20Filled />,
    adminOnly: true,
  },
  {
    to: '/admin/room-blocks',
    label: 'Bloqueos',
    icon: <Wrench20Regular />,
    activeIcon: <Wrench20Filled />,
    adminOnly: true,
  },
  {
    to: '/admin/sincronizacion',
    label: 'Sincronización',
    icon: <ArrowSync20Regular />,
    activeIcon: <ArrowSync20Filled />,
    adminOnly: true,
  },
  {
    to: '/admin/audit',
    label: 'Auditoría',
    icon: <History20Regular />,
    activeIcon: <History20Filled />,
    adminOnly: true,
  },
];

/** Bottom bar keeps four destinations max to preserve 44px touch targets. */
export const MOBILE_NAV: readonly NavItem[] = PRIMARY_NAV;
